import { getStorage, ref, uploadBytesResumable } from 'firebase/storage';
import { app } from '../config/firebase';
import { FIREBASE_STORAGE_TEMP_IMAGES_PATH } from '../config/constants';

const storage = getStorage(app);

/**
 * Uploads a temporary image to Firebase Storage
 * Used for user-uploaded images during the purchase flow
 *
 * @param {string} uid - User ID
 * @param {string} patternId - Pattern ID (document ID)
 * @param {string} dataURL - Image data URL (base64 encoded)
 * @param {string} suffix - (optional) Suffix for the filename (e.g., '-2' for secondary image)
 *
 * @returns {Promise<object>} { path: string } - The storage path of the uploaded image
 */
const normalizeToJpeg = (dataURL) =>
	new Promise((resolve, reject) => {
		const img = new Image();
		img.onload = () => {
			const canvas = document.createElement('canvas');
			canvas.width = img.naturalWidth;
			canvas.height = img.naturalHeight;
			const ctx = canvas.getContext('2d');
			ctx.fillStyle = '#ffffff';
			ctx.fillRect(0, 0, canvas.width, canvas.height);
			ctx.drawImage(img, 0, 0);
			resolve(canvas.toDataURL('image/jpeg', 0.92));
		};
		img.onerror = reject;
		img.src = dataURL;
	});

export const uploadTempImage = async (uid, patternId, dataURL, suffix = '') => {
	// Normalize to JPEG before upload to avoid format issues (e.g. PNGs with
	// trailing metadata that cause Jimp/pngjs to throw "unrecognised content at end of stream")
	const normalizedDataURL = await normalizeToJpeg(dataURL);

	// Convert data URL to blob
	const response = await fetch(normalizedDataURL);
	const blob = await response.blob();

	// Create storage path
	const fileName = `${patternId}${suffix}.jpg`;
	const storagePath = `${FIREBASE_STORAGE_TEMP_IMAGES_PATH}/${uid}/${fileName}`;

	// Create reference and upload
	const fileRef = ref(storage, storagePath);
	const uploadTask = uploadBytesResumable(fileRef, blob);

	// Return promise that resolves when upload completes
	return new Promise((resolve, reject) => {
		uploadTask.on(
			'state_changed',
			(snapshot) => {
				// Track upload progress if needed
				const progress =
					(snapshot.bytesTransferred / snapshot.totalBytes) * 100;
				// eslint-disable-next-line no-console
				console.log(`Upload progress: ${progress}%`);
			},
			(error) => {
				// eslint-disable-next-line no-console
				console.error('Upload error:', error);
				reject(error);
			},
			() => {
				// Upload complete
				resolve({ path: storagePath });
			},
		);
	});
};
